import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import {
  Sparkles,
  CheckCircle2,
} from 'lucide-react';
import { logEvent } from '../utils/analytics';
import WaitlistModal from './BetaTesterModal'; // adjust path if needed

const SCREENSHOTS = [
  '/dashboard1.jpeg',
  '/mealInsights2.jpeg',
  '/coachHome.jpeg',
  '/logFood.jpeg',
];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

export default function Hero() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % SCREENSHOTS.length);
    }, 4000);

    return () => clearInterval(timer);
  }, []);

  const openWaitlistModal = () => {
    logEvent('Conversion', 'Open_Waitlist_Modal', 'Hero_Section');
    setIsModalOpen(true);
  };

  return (
    <>
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.10),transparent_34%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_20%,rgba(16,185,129,0.08),transparent_28%)]" />
          <div className="absolute left-[-8%] top-16 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="absolute right-[-10%] top-24 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:48px_48px]" />
        </div>

        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 pb-24 pt-16 md:pt-24 lg:grid-cols-2 lg:gap-16">
          {/* LEFT COLUMN */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="z-10 text-center lg:text-left"
          >
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-primary)]/20 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),rgba(16,185,129,0.08))] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.22em] text-[var(--color-brand-primary)] shadow-[0_8px_30px_rgba(34,211,238,0.08)]">
              <Sparkles size={14} />
              AI weight management
            </div>

            <h1 className="text-5xl font-black leading-[1.02] tracking-tight text-[var(--color-brand-text)] md:text-6xl xl:text-7xl">
              Stop logging blindly.
              <span className="mt-2 block bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] bg-clip-text text-transparent">
                Start understanding your day.
              </span>
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-[var(--color-brand-text-secondary)] lg:mx-0 md:text-xl">
              LesslyKilo turns meals, hydration, workouts, and weight into clear daily guidance.
              Track faster, spot patterns earlier, and make smarter weight-loss decisions with AI-powered insight.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
              {[
                'Daily calorie guidance',
                'Meal & hydration insights',
                'Coach support on demand',
              ].map((item) => (
                <div
                  key={item}
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] px-4 py-2 text-sm font-semibold text-[var(--color-brand-text)] shadow-[0_8px_24px_rgba(15,23,42,0.06)]"
                >
                  <CheckCircle2 size={16} className="text-emerald-400" />
                  {item}
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start">
              <button
                type="button"
                onClick={openWaitlistModal}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] px-8 py-4 text-lg font-black text-white shadow-[0_14px_30px_rgba(34,211,238,0.22)] transition-all duration-300 hover:scale-[1.02] hover:brightness-110 sm:w-auto"
              >
                <Sparkles size={20} className="transition-transform group-hover:-translate-y-0.5" />
                Join the waitlist
              </button>
            </div>

            <p className="mt-4 text-sm text-[var(--color-brand-text-secondary)]">
              First 100 users to join the waitlist get lifetime free premium access.
            </p>
          </motion.div>

          {/* RIGHT COLUMN */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.1 }}
            className="relative mx-auto w-full max-w-[380px] lg:max-w-[430px]"
          >
            <div className="pointer-events-none absolute inset-0 -z-10">
              <div className="absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--color-brand-primary)]/15 blur-[80px]" />
              <div className="absolute right-4 top-10 h-32 w-32 rounded-full bg-emerald-400/12 blur-3xl" />
            </div>

            <div className="relative mx-auto w-full max-w-[320px]">
              <div className="absolute -left-6 top-16 z-20 hidden rounded-2xl border border-[var(--color-brand-border)] bg-[linear-gradient(180deg,var(--color-brand-card),var(--color-brand-ai-bg))] px-4 py-3 shadow-[0_16px_40px_rgba(15,23,42,0.10)] md:block">
                <div className="text-[11px] font-black uppercase tracking-[0.18em] text-[var(--color-brand-primary)]">
                  Daily snapshot
                </div>
                <div className="mt-1 text-sm font-semibold text-[var(--color-brand-text)]">
                  Track. Understand. Adjust.
                </div>
              </div>

              <div className="absolute -right-8 bottom-24 z-20 hidden rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 shadow-[0_16px_40px_rgba(15,23,42,0.10)] md:block">
                <div className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-500">
                  AI support
                </div>
                <div className="mt-1 text-sm font-semibold text-[var(--color-brand-text)]">
                  Better next decisions
                </div>
              </div>

              <div
                className="relative mx-auto aspect-[9/19.5] w-full transition-transform duration-700"
                style={{
                  transformStyle: 'preserve-3d',
                  transform: 'rotateY(-14deg) rotateX(10deg) rotateZ(3deg)',
                }}
              >
                <div className="absolute inset-0 rounded-[3rem] border-[10px] border-[#111827] bg-[#0b1220] shadow-[0_30px_80px_rgba(0,0,0,0.35)] ring-1 ring-white/10">
                  <div className="absolute left-1/2 top-0 z-20 h-7 w-32 -translate-x-1/2 rounded-b-2xl bg-[#111827]" />

                  <div className="relative h-full w-full overflow-hidden rounded-[2.25rem] bg-black">
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={currentIndex}
                        src={SCREENSHOTS[currentIndex]}
                        alt={`LesslyKilo screenshot ${currentIndex + 1}`}
                        initial={{ opacity: 0, scale: 1.03 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.45 }}
                        className="absolute inset-0 h-full w-full object-cover object-top"
                      />
                    </AnimatePresence>

                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10" />
                  </div>

                  <div className="absolute -left-[14px] top-28 h-16 w-[4px] rounded-l-md bg-[#2A2A2A]" />
                  <div className="absolute -left-[14px] top-48 h-16 w-[4px] rounded-l-md bg-[#2A2A2A]" />
                  <div className="absolute -right-[14px] top-36 h-20 w-[4px] rounded-r-md bg-[#2A2A2A]" />
                </div>
              </div>

              <div className="mt-8 flex items-center justify-center gap-2">
                {SCREENSHOTS.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentIndex(idx)}
                    aria-label={`Go to screenshot ${idx + 1}`}
                    className={`h-2.5 rounded-full transition-all ${
                      idx === currentIndex
                        ? 'w-8 bg-[var(--color-brand-primary)]'
                        : 'w-2.5 bg-[var(--color-brand-border)]'
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <WaitlistModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}